import { Card } from "@/types/v2";
import { Clock } from "lucide-react";
import { Fragment } from "react";
import { cn, firestoreToDateTime } from "@/lib/utils";
import { container } from "@/repositories/container";
import { useCardMetricsOptions } from "@/hooks/useCardMetricsOptions";
import { Switch } from "@/components/Base/Switch";
import { Label } from "@/components/Base/Label";
import {
  customFormatDuration,
  getContainerDuration,
  getTotalTimeInShop,
  getAggregatedContainerDurations,
} from "@/lib/cardMetrics";

export interface CardMetricsReportProps {
  cardData: Card;
}

export const CardMetricsReport = ({ cardData }: CardMetricsReportProps) => {
  const {
    timestampsEnabled,
    setTimestampsEnabled,
    setAggregateTimesEnabled,
    aggregateTimesEnabled,
  } = useCardMetricsOptions();

  const AggregateCardHistory = () => {
    const aggregateCardHistory = getAggregatedContainerDurations(
      cardData.history
    );

    return (
      <Fragment>
        {aggregateCardHistory.map((containerInfo, index) => (
          <div key={index} className={"grid grid-cols-3 gap-2"}>
            <div className="text-left">
              {container.getContainerName(containerInfo.containerId)}
            </div>
            <div className={cn("col-span-2 w-full text-right")}>
              {customFormatDuration(containerInfo.totalDuration)}
            </div>
          </div>
        ))}
      </Fragment>
    );
  };

  const CardHistory = () => {
    return (
      <Fragment>
        {cardData?.history?.map((containerInfo, index) => (
          <div key={index} className={"grid grid-cols-3 gap-2"}>
            <div className="text-left">
              {container.getContainerName(containerInfo.currentContainerId)}
            </div>
            {timestampsEnabled && (
              <div className="text-left">
                {firestoreToDateTime(containerInfo.enteredAt)?.toLocaleString()}
              </div>
            )}
            <div
              className={cn(
                "w-full text-right",
                !timestampsEnabled && "col-span-2"
              )}
            >
              {customFormatDuration(getContainerDuration(containerInfo))}
            </div>
          </div>
        ))}
      </Fragment>
    );
  };

  return (
    <>
      {/* RO Report */}
      <div className="mt-4 flex flex-row items-center justify-between border-t py-2">
        <h2 className={"text-xl font-bold"}>RO Report</h2>
        {!aggregateTimesEnabled && (
          <div className="flex flex-row items-center justify-start gap-4">
            <Label>Show Timestamps</Label>
            <Switch
              checked={timestampsEnabled}
              onCheckedChange={setTimestampsEnabled}
            />
          </div>
        )}
        <div className="flex flex-row items-center justify-start gap-4">
          <Label>Combine Containers</Label>
          <Switch
            checked={aggregateTimesEnabled}
            onCheckedChange={setAggregateTimesEnabled}
          />
        </div>
      </div>

      {/* Header Row */}
      {cardData?.history?.length > 0 ? (
        <>
          <div className="grid grid-cols-3 gap-4 pb-2 font-semibold">
            <div className="text-left">Container</div>
            {timestampsEnabled && !aggregateTimesEnabled && (
              <div className="text-left">Entered At</div>
            )}
            <div
              className={cn(
                "text-right",
                !timestampsEnabled && "col-span-2",
                aggregateTimesEnabled && "col-span-2"
              )}
            >
              Duration
            </div>
          </div>

          {/* Data Rows */}
          <div className="max-h-96 overflow-y-auto">
            {aggregateTimesEnabled ? <AggregateCardHistory /> : <CardHistory />}
          </div>

          {/* Footer */}
          <div className="grid grid-cols-3 pb-2 font-semibold">
            {/* TODO: Calculate total time */}
            <div className="text-left">Total Container Time</div>
            <div className="col-span-2 w-full text-right">
              {getTotalTimeInShop(cardData.history)}
            </div>
          </div>
        </>
      ) : (
        <div className="text-center">No container history available</div>
      )}

      {/* Total Time in Shop */}
      <div className="mt-4 flex h-10 items-center justify-center rounded-b-lg bg-black text-white">
        <Clock className="pr-2" /> Total Time in Shop{" "}
        {getTotalTimeInShop(cardData.history)}
      </div>
    </>
  );
};
